<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-about-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header -->
      <v-card-title class="px-6 py-4 editor-header d-flex justify-space-between align-center">
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-account-edit</v-icon>
          Edit About Me
        </div>
        <v-btn icon @click="onClose" class="close-btn" color="white" depressed large>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Share information about yourself and your professional background
        </div>
      </v-card-text>

      <!-- Error/Success -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="messageType === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'"
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- About Me -->
      <v-card-text class="px-6 py-2">
        <div class="mb-3 text-subtitle-1 font-weight-medium primary--text d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-text</v-icon>
          Personal Introduction
        </div>

        <v-textarea
          v-model="profile.about_me"
          outlined
          auto-grow
          rows="6"
          placeholder="Tell others about your background, experience, and professional interests..."
          class="modern-textarea"
          :rules="[rules.required, rules.noXSS]"
          counter="1000"
          maxlength="1000"
          @input="onChange"
          no-resize
        >
          <template v-slot:label>
            <span class="text--secondary">Your professional story</span>
          </template>
        </v-textarea>

        <div class="mt-1 text-right text-caption grey--text">
          {{ profile.about_me ? profile.about_me.length : 0 }}/1000 characters
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="px-6 pt-4 pb-6 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          width="160"
          :loading="loading"
          :disabled="!profile.about_me"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save Profile
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";

// 🛡️ Helper: sanitize before save
function cleanProfile(profile) {
  const sanitize = str =>
    str ? str.replace(/[<>]/g, "") : ""; // remove < and >
  return {
    ...profile,
    about_me: sanitize(profile.about_me)
  };
}

export default {
  props: {
    dialog: { type: Boolean, default: false },
    onClose: { type: Function }
  },
  data() {
    return {
      message: null,
      messageType: "error",
      loading: false,
      rules: {
        required: value => !!value || "This field is required",
        noXSS: value =>
          !/[<>]/.test(value) || "Invalid characters detected"
      },
      profile: {}
    };
  },
  methods: {
    ...mapActions("auth", { updateProfile: "updateProfile" }),
    onChange() {
      this.message = null;
    },
    async onSave() {
      if (!this.profile.about_me) {
        this.message = "Please tell others about yourself";
        this.messageType = "error";
        return;
      }

      // 🔒 Clean before sending
      const safeProfile = cleanProfile(this.profile);

      this.loading = true;
      try {
        await this.updateProfile({ about_me: safeProfile.about_me });
        this.message = "Your profile was updated successfully!";
        this.messageType = "success";
        setTimeout(() => {
          this.onClose();
        }, 1500);
      } catch (error) {
        this.message =
          error.response?.data?.message || error.message || "An error occurred";
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    }
  },
  computed: {
    ...mapState("auth", { myProfile: "profile" })
  },
  watch: {
    myProfile(newValue) {
      this.profile = { ...newValue };
    }
  },
  mounted() {
    this.profile = { ...this.myProfile };
    this.message = null;
  }
};
</script>


<style scoped>
.modern-about-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255,255,255,0.2) !important;
}

.modern-textarea::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  /* border: 2px solid #e0e0e0; */
  background-color: white !important;
}

.modern-textarea::v-deep .v-input__slot:hover {
  border-color: var(--v-primary-base);
}

.modern-textarea::v-deep .v-text-field__slot textarea {
  padding: 14px;
  line-height: 1.7 !important;
}

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-about-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-about-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-about-dialog::-webkit-scrollbar-thumb {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-about-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}
</style>